import { FC } from 'react';
import { Box, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { detectDirection } from '@libs/utils';
import { CheckIcon, PencilSimpleLineIcon, XIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { CHAT_COLUMN_GUTTER } from '../constants';

import { ChatNameProps } from './ChatPage';

// ChatHeaderProps interface that contains all states/props to manage the Chat Header section of the ChatPage component
export type ChatHeaderProps = ChatNameProps;

export const ChatHeader: FC<ChatHeaderProps> = observer(
  ({
    isEditingChatName,
    isLoadingChatNameSaving,
    editedChatName,
    onChatNameInputChange,
    onStopEditingChatName,
    onSaveChatName,
    chatName,
    onStartEditingChatName,
  }) => {
    const { t } = useTrans();
    const dynamiDirection = detectDirection(chatName);

    // This will handle the key press events of "enter" for saving the chat name input and "escape" for stopping editing chat name
    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSaveChatName();
        return;
      }

      if (e.key === 'Escape' && !e.shiftKey) {
        e.preventDefault();
        onStopEditingChatName();
        return;
      }
    };

    // `px` matches the thread's gutter, so the title — and the divider drawn on
    // the inner row — start and end on the message column's edges.
    return (
      <Box sx={{ px: CHAT_COLUMN_GUTTER, pt: 1.5 }}>
        <NoFlipBox
          mode={dynamiDirection}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            pb: 1.5,
            borderBottom: '1px solid #F1F1F4',
          }}
        >
          {/* If the chat name is being edited then show the text field and the buttons to save and stop editing */}
          {isEditingChatName ? (
            <Stack
              gap={1.5}
              direction={{ xs: 'column', md: 'row' }}
              alignItems="center"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <TextField
                disabled={isLoadingChatNameSaving}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                fullWidth
                value={editedChatName}
                onChange={(e) => onChatNameInputChange(e.target.value)}
                onKeyDown={handleKeyPress}
                autoFocus
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'common.white',
                    fontWeight: 400,
                    fontSize: '1rem',
                  },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={onStopEditingChatName}
                  disabled={isLoadingChatNameSaving}
                  size="small"
                  sx={{ py: 1, px: 2, color: '#171717' }}
                >
                  <XIcon size={20} />
                </IconButton>
                <IconButton
                  onClick={onSaveChatName}
                  disabled={isLoadingChatNameSaving || !editedChatName.trim()}
                  size="small"
                  sx={{
                    backgroundColor: '#171717',
                    borderRadius: 2,
                    color: '#ffffff',
                    py: 1,
                    px: 2,
                    '&:hover': { backgroundColor: '#171717' },
                    '&.Mui-disabled': { backgroundColor: '#483D3D42' },
                  }}
                >
                  {/* If the chat name is being saved then show the loading indicator else show the check icon */}
                  {isLoadingChatNameSaving ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CheckIcon size={20} />
                  )}
                </IconButton>
              </Box>
            </Stack>
          ) : (
            // If the chat name is not being edited then show the chat name and the button to start editing the chat name
            <>
              <Typography
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  fontSize: '1.063rem',
                  '@keyframes chatTitleIn': {
                    from: { opacity: 0, transform: 'translateY(-8px)' },
                    to: { opacity: 1, transform: 'none' },
                  },
                  animation: 'chatTitleIn .34s cubic-bezier(.22,.8,.3,1) both',
                }}
              >
                {chatName}
              </Typography>
              <IconButton
                onClick={onStartEditingChatName}
                size="small"
                aria-label={t('chat.header.rename') as string}
                sx={{
                  flexShrink: 0,
                  color: 'text.tertiary',
                  '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
                }}
              >
                <PencilSimpleLineIcon size={16} />
              </IconButton>
            </>
          )}
        </NoFlipBox>
      </Box>
    );
  },
);
